import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  academicYears as yearsApi,
  sundayGrades as gradesApi,
  sundayClasses as classesApi,
  sundayTeachers as teachersApi,
  sundayAttendance as attendanceApi,
  sundayOfferings as offeringsApi,
  sundayExams as examsApi,
  sundayExamMarks as marksApi,
  batch
} from '../../api/endpoints';
import type { BatchOperation } from '../../api/endpoints';
import { getList } from '../../api/client';
import type {
  AcademicYear, AcademicClass, SundayGrade, Teacher, Exam, ExamMarks, ExamScore, WeeklyRegister, SundayOfferingRow
} from '../../types';
import type { Notify } from '../../hooks/useNotification';
import { nextRecordId, normalizeYearStatus, voidStamp } from './registers';

// Sunday School spans seven tables. This adapter loads them together and
// presents each as a `{ items, add, update }` collection so the screen works
// with one shape regardless of how the rows are stored.
//
// Two of those tables are denormalised on the server:
//   - attendance is a JSON blob keyed by student record id
//   - a week's offering lives in its own table, keyed by (date, classId)
// so a "weekly register" row is stitched together here and split again on save.
//
// Cross-references are by `recordId` (e.g. `SSC-1000`), never the numeric
// primary key — the key is kept as `rawId` for updates.
//
// Every write goes back as the WHOLE row. The server replaces the record with
// what it is sent, so an update built from form fields alone would blank
// recordId, status and createdBy — and every class hanging off a year by its
// recordId would come loose. `merge` below is what stops that.

/**
 * Rows carry the human id as `id`, with the database key alongside as `rawId`.
 * The numeric `id` is omitted so the two can't be confused — everything in this
 * screen cross-references by the human id.
 */
export type SsRow<T> = Omit<T, 'id'> & { id?: string; rawId?: number };
type Mapped<T> = SsRow<T>;

const ACTIVE = 'ACTIVE';
const VOID = 'VOID';

/** The eight tables, by the name each collection is known by here. */
type TableKey = 'years' | 'grades' | 'classes' | 'teachers' | 'exams' | 'attendance' | 'offerings' | 'marks';
const ALL_TABLES: TableKey[] = ['years', 'grades', 'classes', 'teachers', 'exams', 'attendance', 'offerings', 'marks'];

/** `{"STU-1": true}` -> object. Bad JSON yields an empty map, never a crash. */
const parseJson = <T,>(raw: string | undefined, fallback: T): T => {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

/** One grade to create, with the section letters it starts with. */
export interface GradeDraft {
  grade: Partial<SundayGrade>;
  letters: string[];
}

export interface Collection<T> {
  items: SsRow<T>[];
  add: (item: Partial<T>) => Promise<boolean>;
  /** Update an existing row by its database key (rawId). */
  update: (rawId: number, item: Partial<T>) => Promise<boolean>;
}

export interface SundaySchoolData {
  loading: boolean;
  refetch: () => Promise<void>;
  years: Collection<AcademicYear> & {
    /** Make this the one Active year; every other year becomes Archived. */
    setActive: (recordId: string) => Promise<boolean>;
  };
  grades: Collection<SundayGrade> & {
    /**
     * Creates several grades, each with its first sections (A, B, ...), in
     * one go — ids for all of them are numbered in a single sequence.
     */
    addBatch: (batch: GradeDraft[]) => Promise<boolean>;
    void: (rawId: number, reason: string) => Promise<boolean>;
  };
  classes: Collection<AcademicClass> & {
    /** Creates one section per letter under a grade, numbered in one go. */
    addSections: (grade: SsRow<SundayGrade>, letters: string[]) => Promise<boolean>;
    void: (rawId: number, reason: string) => Promise<boolean>;
  };
  teachers: Collection<Teacher>;
  exams: Collection<Exam> & {
    /** Several exams at once, ids numbered in one sequence. */
    addBatch: (items: Partial<Exam>[]) => Promise<boolean>;
    void: (rawId: number, reason: string) => Promise<boolean>;
  };
  /** Already keyed by the human id, so it needs no further mapping. */
  weekly: {
    /** One row per attendance sheet, with that week's offerings summed in. */
    items: WeeklyRegister[];
    /** Registers that were voided, with their reason. */
    voidedItems: WeeklyRegister[];
    /** Raw offering rows, one per record — never merged, so per-student
     *  offerings on the same class/date stay distinct (CHM-082). */
    offerings: SundayOfferingRow[];
    voidedOfferings: SundayOfferingRow[];
    /** Attendance sheet, plus the offering if one was entered alongside it. */
    add: (item: Partial<WeeklyRegister>) => Promise<boolean>;
    /** Re-save an existing sheet; the class-total offering is upserted. */
    update: (attendanceRawId: number, item: Partial<WeeklyRegister>) => Promise<boolean>;
    voidRegister: (attendanceRawId: number, reason: string) => Promise<boolean>;
    /** Collection only — no attendance sheet is written. */
    addOffering: (item: Partial<WeeklyRegister>) => Promise<boolean>;
    updateOffering: (rawId: number, item: Partial<WeeklyRegister>) => Promise<boolean>;
    voidOffering: (rawId: number, reason: string) => Promise<boolean>;
  };
  marks: {
    items: ExamMarks[];
    /** One row per exam per section — re-saving replaces that section's scores. */
    save: (examId: string, classId: string, scores: Record<string, ExamScore>) => Promise<boolean>;
  };
}

export const useSundaySchoolData = (notify?: Notify): SundaySchoolData => {
  const [loading, setLoading] = useState(true);
  // Every row the server holds, voided ones included. The screen sees only the
  // live ones, but ids are numbered over the lot — a voided SSA-1007 must never
  // be handed out again.
  const [allYears, setAllYears] = useState<Mapped<AcademicYear>[]>([]);
  const [allGrades, setAllGrades] = useState<Mapped<SundayGrade>[]>([]);
  const [allClasses, setAllClasses] = useState<Mapped<AcademicClass>[]>([]);
  const [allTeachers, setAllTeachers] = useState<Mapped<Teacher>[]>([]);
  const [allExams, setAllExams] = useState<Mapped<Exam>[]>([]);
  const [allAttendance, setAllAttendance] = useState<any[]>([]);
  const [allOfferings, setAllOfferings] = useState<any[]>([]);
  const [allMarks, setAllMarks] = useState<any[]>([]);

  const announce = useCallback(
    (message: string, type?: 'success' | 'error') => { if (notify) notify(message, type); },
    [notify]
  );

  /**
   * Re-reads the named tables — or all eight when none are named. A write
   * reloads only the table it touched: marking one Sunday must not pull every
   * attendance row, offering and mark sheet the school has ever kept.
   */
  const reload = useCallback(async (...keys: TableKey[]) => {
    const wanted = new Set<TableKey>(keys.length ? keys : ALL_TABLES);
    if (keys.length === 0) setLoading(true);
    // A missing table must not blank the whole screen, so each read degrades
    // to an empty list on failure.
    const safe = <T,>(p: Promise<T>) => p.catch(() => [] as unknown as T);
    const map = <T,>(rows: any[]): Mapped<T>[] =>
      rows.map((r) => ({ ...r, id: r.recordId, rawId: r.id }));
    const readers: Record<TableKey, () => Promise<void>> = {
      // Years were saved as both "Active" and "ACTIVE" over time; the screen
      // compares against one spelling, so they are read as one spelling.
      years: async () => setAllYears(map<AcademicYear>(getList<any>(await safe(yearsApi.list()))).map((r) => ({ ...r, status: normalizeYearStatus(r.status) }))),
      grades: async () => setAllGrades(map<SundayGrade>(getList(await safe(gradesApi.list())))),
      classes: async () => setAllClasses(map<AcademicClass>(getList(await safe(classesApi.list())))),
      teachers: async () => setAllTeachers(map<Teacher>(getList(await safe(teachersApi.list())))),
      exams: async () => setAllExams(map<Exam>(getList(await safe(examsApi.list())))),
      attendance: async () => setAllAttendance(getList(await safe(attendanceApi.list()))),
      offerings: async () => setAllOfferings(getList(await safe(offeringsApi.list()))),
      marks: async () => setAllMarks(getList(await safe(marksApi.list())))
    };
    await Promise.all(Array.from(wanted).map((k) => readers[k]()));
    if (keys.length === 0) setLoading(false);
  }, []);
  const refetch = useCallback(() => reload(), [reload]);

  useEffect(() => { refetch(); }, [refetch]);

  const live = <T extends { status?: unknown }>(rows: T[]) => rows.filter((r) => r.status !== VOID);
  const rawYears = useMemo(() => live(allYears), [allYears]);
  const rawGrades = useMemo(() => live(allGrades), [allGrades]);
  const rawClasses = useMemo(() => live(allClasses), [allClasses]);
  const rawTeachers = useMemo(() => live(allTeachers), [allTeachers]);
  const rawExams = useMemo(() => live(allExams), [allExams]);
  const rawAttendance = useMemo(() => live(allAttendance), [allAttendance]);
  const rawOfferings = useMemo(() => live(allOfferings), [allOfferings]);
  const rawMarks = useMemo(() => live(allMarks), [allMarks]);

  // --- Weekly register: one UI row per attendance sheet across two tables ---
  //
  // Offerings for the same class and date are SUMMED onto the sheet: a class
  // total and three per-student gifts on one Sunday are four rows in the
  // offerings table but one amount collected that week. (They used to overwrite
  // each other, so the register showed whichever row loaded last.)
  //
  // Sheets are keyed by their own record, not by date+class, so a duplicate
  // sheet — the same class marked twice on one Sunday — is visible and can be
  // voided, instead of hiding behind the other while its money still counted.
  const buildRegisters = useCallback((sheets: any[], offerings: any[]): WeeklyRegister[] => {
    const offeringsByKey = new Map<string, any[]>();
    offerings.forEach((r) => {
      const key = `${r.date}|${r.classId}`;
      offeringsByKey.set(key, [...(offeringsByKey.get(key) || []), r]);
    });
    const sumOf = (rows: any[]) => rows.reduce((s, r) => s + (parseFloat(String(r.amount)) || 0), 0);
    const classTotalOf = (rows: any[]) => rows.find((r) => !r.studentId);

    const seenKeys = new Set<string>();
    const out: WeeklyRegister[] = sheets.map((r) => {
      const key = `${r.date}|${r.classId}`;
      seenKeys.add(key);
      const week = offeringsByKey.get(key) || [];
      return {
        id: r.recordId || String(r.id),
        date: r.date,
        classId: r.classId,
        attendance: parseJson<Record<string, boolean>>(r.attendanceData, {}),
        offering: sumOf(week),
        attendanceRawId: r.id,
        offeringRawId: classTotalOf(week)?.id,
        createdBy: r.createdBy,
        deleteReason: r.deleteReason,
        deletedAt: r.deletedAt
      };
    });

    // An offering recorded without an attendance sheet still shows up.
    offeringsByKey.forEach((week, key) => {
      if (seenKeys.has(key)) return;
      const first = week[0];
      out.push({
        id: first.recordId || key,
        date: first.date,
        classId: first.classId,
        attendance: {},
        offering: sumOf(week),
        offeringRawId: classTotalOf(week)?.id
      });
    });

    return out.sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
  }, []);

  const weeklyItems = useMemo(
    () => buildRegisters(rawAttendance, rawOfferings),
    [buildRegisters, rawAttendance, rawOfferings]
  );
  // Voided sheets are listed on their own with no money attached — the offering
  // rows have their own void, and a voided sheet is not what collected them.
  const voidedWeeklyItems = useMemo(
    () => buildRegisters(allAttendance.filter((r) => r.status === VOID), []),
    [buildRegisters, allAttendance]
  );

  // Raw offering rows, kept individual (a class total and each per-student
  // offering stay separate, unlike the class/date-merged register above).
  const mapOfferings = (rows: any[]): SundayOfferingRow[] => rows
    .map((r) => ({
      id: r.recordId || String(r.id),
      rawId: r.id,
      date: r.date,
      classId: r.classId,
      studentId: r.studentId || '',
      offering: r.amount,
      createdBy: r.createdBy,
      donationId: r.donationId || undefined,
      deleteReason: r.deleteReason,
      deletedAt: r.deletedAt
    }))
    .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
  const offeringItems = useMemo(() => mapOfferings(rawOfferings), [rawOfferings]);
  const voidedOfferingItems = useMemo(
    () => mapOfferings(allOfferings.filter((r) => r.status === VOID)),
    [allOfferings]
  );

  const marksItems = useMemo<ExamMarks[]>(
    () => rawMarks.map((r) => ({
      id: r.recordId,
      examId: r.examId,
      classId: r.classId || undefined,
      scores: parseJson<Record<string, ExamScore>>(r.scoresData, {}),
      rawId: r.id,
      recordId: r.recordId
    })),
    [rawMarks]
  );

  /** Reports a failure the same way for every write, then refreshes. */
  /** Runs a write, then re-reads just the tables it changed. */
  const attempt = async (label: string, run: () => Promise<unknown>, ...touched: TableKey[]): Promise<boolean> => {
    try {
      await run();
      await reload(...touched);
      return true;
    } catch (err) {
      announce((err as Error).message || `Could not save the ${label}.`, 'error');
      return false;
    }
  };

  /** Wraps a create so every collection reports failures the same way. */
  const creator = <T,>(
    label: string,
    run: (item: Partial<T>) => Promise<unknown>,
    ...touched: TableKey[]
  ) => (item: Partial<T>): Promise<boolean> => attempt(label, () => run(item), ...touched);

  /**
   * The server row as it is now, with the database key stripped back to `id`
   * the way the API expects it, so an edit sends every column and not just
   * the ones on the form.
   */
  const merge = (existing: any, changes: Record<string, unknown>) => {
    const { rawId, ...row } = existing || {};
    return { ...row, ...changes, id: rawId ?? existing?.id };
  };

  /**
   * The edit counterpart of `creator` (CHM-076 — there was no way to change a
   * created row). Looks the current row up by its key and merges the form
   * over it, so nothing the form does not show is lost on the way back.
   */
  const updater = <T,>(
    label: string,
    api: { update: (id: number, body: any) => Promise<unknown> },
    rows: () => Array<{ rawId?: number }>,
    touched: TableKey
  ) => (rawId: number, item: Partial<T>): Promise<boolean> =>
    attempt(label, () => {
      const existing = rows().find((r) => r.rawId === rawId);
      if (!existing) throw new Error(`That ${label} no longer exists. Refresh and try again.`);
      return api.update(rawId, merge(existing, item as Record<string, unknown>));
    }, touched);

  // --- Academic years -------------------------------------------------------
  // Only one year is ever Active. Creating a year makes it the active one and
  // archives the rest; `setActive` moves the marker later (e.g. after creating
  // next year early, before it has actually started). Each change is saved
  // together (or sent for approval together).
  const archiveAllExcept = (keepRecordId: string): BatchOperation[] =>
    rawYears.filter((y) => y.recordId !== keepRecordId && y.status === 'Active')
      .map((y) => batch.change(yearsApi, y.rawId as number, merge(y, { status: 'Archived' }) as any));

  const addYear = creator<AcademicYear>('academic year', async (item) => {
    const recordId = nextRecordId('SSY', allYears);
    await batch.run([batch.add(yearsApi, { ...item, recordId, status: 'Active' }), ...archiveAllExcept(recordId)],
      `New academic year ${item.name || recordId}`);
  }, 'years');

  const setActiveYear = (recordId: string) => attempt('academic year', async () => {
    const target = rawYears.find((y) => y.recordId === recordId);
    if (!target) throw new Error('That academic year no longer exists.');
    const together = archiveAllExcept(recordId);
    if (target.status !== 'Active') {
      together.push(batch.change(yearsApi, target.rawId as number, merge(target, { status: 'Active' }) as any));
    }
    if (together.length) await batch.run(together, `Make ${target.name || recordId} the active academic year`);
  }, 'years');

  const numberOf = (recordId: string) => parseInt(recordId.split('-')[1], 10);

  /** The sections as saves, numbered from `first` (in order: the server assigns ids in order of arrival). */
  const sectionOps = (
    grade: Omit<Partial<SundayGrade>, 'id'> & { id?: string },
    letters: string[],
    first = numberOf(nextRecordId('SSC', allClasses))
  ): BatchOperation[] => letters.map((letter, i) => batch.add(classesApi, {
    recordId: `SSC-${first + i}`,
    className: `${grade.name} – ${letter}`,
    academicYearId: grade.academicYearId,
    gradeId: grade.id,
    section: letter,
    location: grade.location || '',
    status: ACTIVE
  } as any));
  const addGrade = creator<SundayGrade>('grade', (item) =>
    gradesApi.create({ ...item, recordId: nextRecordId('SSG', allGrades), status: ACTIVE } as any), 'grades');

  /**
   * Several grades and their sections in one pass, saved together (or sent for
   * approval together). Every id comes from one running count — a loop of
   * single creates would read the same (stale) table each time and hand every
   * grade the same number.
   */
  const addGradesBatch = (drafts: GradeDraft[]) => attempt('grades', async () => {
    const firstGrade = numberOf(nextRecordId('SSG', allGrades));
    let nextClass = numberOf(nextRecordId('SSC', allClasses));
    const together: BatchOperation[] = [];
    drafts.forEach(({ grade, letters }, i) => {
      const recordId = `SSG-${firstGrade + i}`;
      const { id: _ignored, ...fields } = grade;
      together.push(batch.add(gradesApi, { ...fields, recordId, status: ACTIVE } as any));
      together.push(...sectionOps({ ...fields, id: recordId }, letters, nextClass));
      nextClass += letters.length;
    });
    await batch.run(together, drafts.length === 1
      ? `New grade ${drafts[0].grade.name} with ${drafts[0].letters.length} section(s)`
      : `${drafts.length} new grades with their sections`);
  }, 'grades', 'classes');

  /** Voids a row of any of the simple tables with the shared audit stamp. */
  const voider = (
    label: string,
    api: { update: (id: number, body: any) => Promise<unknown> },
    rows: () => any[],
    touched: TableKey
  ) => (rawId: number, reason: string) => attempt(label, () => {
    const existing = rows().find((r) => r.rawId === rawId);
    if (!existing) throw new Error(`That ${label} no longer exists.`);
    return api.update(rawId, merge(existing, { status: VOID, deleteReason: reason, deletedAt: voidStamp() }));
  }, touched);

  const addClass = creator<AcademicClass>('class', (item) =>
    classesApi.create({ ...item, recordId: nextRecordId('SSC', allClasses), status: ACTIVE } as any), 'classes');

  /**
   * Sections are created together — "Grade 1" with A, B, C — so their ids
   * are numbered in sequence here rather than each reading the table anew and
   * all landing on the same number.
   */
  const addSections = (grade: SsRow<SundayGrade>, letters: string[]) =>
    attempt('sections', () => batch.run(sectionOps(grade, letters), `Sections ${letters.join(', ')} for ${grade.name}`), 'classes');

  const addTeacher = creator<Teacher>('teacher', (item) =>
    teachersApi.create({ ...item, recordId: nextRecordId('SST', allTeachers), status: ACTIVE } as any), 'teachers');

  const addExam = creator<Exam>('exam', (item) =>
    examsApi.create({ ...item, recordId: nextRecordId('SSE', allExams), status: ACTIVE } as any), 'exams');

  const addExamsBatch = (items: Partial<Exam>[]) => attempt('exams', async () => {
    const first = numberOf(nextRecordId('SSE', allExams));
    for (let i = 0; i < items.length; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      await examsApi.create({ ...items[i], recordId: `SSE-${first + i}`, status: ACTIVE } as any);
    }
  }, 'exams');

  // --- Weekly register writes ----------------------------------------------
  const hasAmount = (v: unknown) => v !== '' && v !== undefined && v !== null;

  const createOffering = (entry: Partial<WeeklyRegister>) =>
    offeringsApi.create({
      recordId: nextRecordId('SSO', allOfferings),
      date: entry.date,
      classId: entry.classId,
      studentId: entry.studentId || '',
      amount: String(entry.offering ?? 0),
      status: ACTIVE
    } as any);

  /** Writes the attendance sheet, and the offering too when one was entered. */
  const addWeekly = creator<WeeklyRegister>('weekly register', async (item) => {
    const week = item as WeeklyRegister;
    await attendanceApi.create({
      recordId: nextRecordId('SSA', allAttendance),
      date: week.date,
      classId: week.classId,
      attendanceData: JSON.stringify(week.attendance || {}),
      status: ACTIVE
    } as any);
    if (hasAmount(week.offering)) await createOffering({ ...week, studentId: '' });
  }, 'attendance', 'offerings');

  /**
   * Re-saves a sheet in place. The offering typed beside it is the CLASS TOTAL
   * for that Sunday: if that row exists it is corrected, otherwise it is
   * created. Per-student offerings for the same day are left alone — they are
   * edited on the Offerings tab.
   */
  const updateWeekly = (attendanceRawId: number, item: Partial<WeeklyRegister>) =>
    attempt('weekly register', async () => {
      const existing = allAttendance.find((r) => r.id === attendanceRawId);
      if (!existing) throw new Error('That register no longer exists. Refresh and try again.');
      const week = item as WeeklyRegister;
      await attendanceApi.update(attendanceRawId, {
        ...existing,
        date: week.date,
        classId: week.classId,
        attendanceData: JSON.stringify(week.attendance || {})
      });
      if (!hasAmount(week.offering)) return;
      const classTotal = allOfferings.find(
        (o) => o.status !== VOID && o.date === existing.date && o.classId === existing.classId && !o.studentId
      );
      if (classTotal) {
        await offeringsApi.update(classTotal.id, {
          ...classTotal, date: week.date, classId: week.classId, amount: String(week.offering)
        });
      } else {
        await createOffering({ ...week, studentId: '' });
      }
    }, 'attendance', 'offerings');

  const voidRegister = (attendanceRawId: number, reason: string) =>
    attempt('weekly register', async () => {
      const existing = allAttendance.find((r) => r.id === attendanceRawId);
      if (!existing) throw new Error('That register no longer exists.');
      await attendanceApi.update(attendanceRawId, {
        ...existing, status: VOID, deleteReason: reason, deletedAt: voidStamp()
      });
    }, 'attendance');

  /**
   * Records a collection on its own. Attendance and offerings are separate
   * screens, so a class can hand in money for a week whose register was never
   * marked — writing an empty attendance sheet here would fake a register.
   */
  const addOffering = creator<WeeklyRegister>('offering', (item) => createOffering(item), 'offerings');

  const updateOffering = (rawId: number, item: Partial<WeeklyRegister>) =>
    attempt('offering', async () => {
      const existing = allOfferings.find((r) => r.id === rawId);
      if (!existing) throw new Error('That collection no longer exists. Refresh and try again.');
      await offeringsApi.update(rawId, {
        ...existing,
        date: item.date,
        classId: item.classId,
        studentId: item.studentId || '',
        amount: String(item.offering ?? 0)
      });
    }, 'offerings');

  const voidOffering = (rawId: number, reason: string) =>
    attempt('offering', async () => {
      const existing = allOfferings.find((r) => r.id === rawId);
      if (!existing) throw new Error('That collection no longer exists.');
      await offeringsApi.update(rawId, {
        ...existing, status: VOID, deleteReason: reason, deletedAt: voidStamp()
      });
    }, 'offerings');

  /**
   * One row per exam per section — re-saving replaces that section's scores.
   * A row saved before marks were kept per section has no classId; it is the
   * row for the (per-section) exam's own section.
   */
  const saveMarks = (examId: string, classId: string, scores: Record<string, ExamScore>) =>
    attempt('marks', async () => {
      const exam = rawExams.find((x) => x.id === examId);
      const existing = rawMarks.find((m) => m.examId === examId
        && (m.classId ? m.classId === classId : exam?.classId === classId));
      const payload = { examId, classId, scoresData: JSON.stringify(scores), status: ACTIVE };
      if (existing) {
        await marksApi.update(existing.id, { ...existing, ...payload } as any);
      } else {
        await marksApi.create({ ...payload, recordId: nextRecordId('SSM', allMarks) } as any);
      }
    }, 'marks');

  return {
    loading,
    refetch,
    years: {
      items: rawYears, add: addYear,
      update: updater<AcademicYear>('academic year', yearsApi, () => rawYears, 'years'),
      setActive: setActiveYear
    },
    grades: {
      items: rawGrades, add: addGrade, addBatch: addGradesBatch,
      update: updater<SundayGrade>('grade', gradesApi, () => rawGrades, 'grades'),
      void: voider('grade', gradesApi, () => rawGrades, 'grades')
    },
    classes: {
      items: rawClasses, add: addClass, addSections,
      update: updater<AcademicClass>('class', classesApi, () => rawClasses, 'classes'),
      void: voider('section', classesApi, () => rawClasses, 'classes')
    },
    teachers: { items: rawTeachers, add: addTeacher, update: updater<Teacher>('teacher', teachersApi, () => rawTeachers, 'teachers') },
    exams: {
      items: rawExams, add: addExam, addBatch: addExamsBatch,
      update: updater<Exam>('exam', examsApi, () => rawExams, 'exams'),
      void: voider('exam', examsApi, () => rawExams, 'exams')
    },
    weekly: {
      items: weeklyItems, voidedItems: voidedWeeklyItems,
      offerings: offeringItems, voidedOfferings: voidedOfferingItems,
      add: addWeekly, update: updateWeekly, voidRegister,
      addOffering, updateOffering, voidOffering
    },
    marks: { items: marksItems, save: saveMarks }
  };
};

export default useSundaySchoolData;
